/**
 * This is a context provider. No story can exist.
 */
/* eslint-disable local-rules/require-storybook-documentation */
import { createContext, useContext, useEffect } from 'react';
import { TreatmentsWithConfig, TreatmentWithConfig } from '@splitsoftware/splitio/types/splitio';
import { FunctionComponentWithChildren } from 'src/utils/types';
import * as ExperimentService from 'src/services/experiments/ExperimentService.client';

export type ExperimentProps = {
    experiments: TreatmentsWithConfig;
    experimentClientInitialized: boolean;
    requestedExperiments: ExperimentService.Domain.ActiveExperiment[];
};

export type UseExperimentResult = {
    experiments: Record<ExperimentService.Domain.ActiveExperiment, TreatmentWithConfig | null>;
    clientInitialized: boolean;
    requestedExperiments: ExperimentService.Domain.ActiveExperiment[];
};

export const ExperimentContext = createContext<ExperimentProps>({
    experiments: {},
    requestedExperiments: [],
    experimentClientInitialized: false,
});

export const useExperiments = (): UseExperimentResult => {
    const {
        experimentClientInitialized: clientInitialized,
        requestedExperiments,
        experiments,
    } = useContext(ExperimentContext);
    const manager = ExperimentService.ExperimentManager.getInstance();
    return {
        clientInitialized,
        requestedExperiments,
        experiments: Object.entries(experiments).reduce<
            Record<ExperimentService.Domain.ActiveExperiment, TreatmentWithConfig | null>
        >(
            (acc, [experimentName, experiment]) => ({
                ...acc,
                [experimentName]: {
                    ...experiment,
                    ...manager.getExperiment(experimentName as ExperimentService.Domain.ActiveExperiment, experiments),
                },
            }),
            {} as Record<ExperimentService.Domain.ActiveExperiment, TreatmentWithConfig | null>
        ),
    };
};

export const ExperimentProvider: FunctionComponentWithChildren<ExperimentProps> = ({ children, ...data }) => {
    const { experiments, requestedExperiments } = data;
    useEffect(() => {
        const manager = ExperimentService.ExperimentManager.getInstance();
        manager.initializeExperimentDatadogTracking(experiments);
    }, [experiments, requestedExperiments]);

    return <ExperimentContext.Provider value={data}>{children}</ExperimentContext.Provider>;
};
